import { Component, inject, ViewChild, OnInit } from '@angular/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import { CalendarComponent } from '../../../shared/components/calendar/calendar.component';
import { MatDividerModule } from '@angular/material/divider';
import { MatButtonModule } from '@angular/material/button';
import { EditDataDialogComponent } from '../../../shared/components/edit-data-dialog/edit-data-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { MatInputModule } from '@angular/material/input';
import { FormsModule } from '@angular/forms';
import { MatRadioModule } from '@angular/material/radio';
import { RestaurantService } from '../../../shared/services/restaurant/restaurant.service';
import { Observable } from 'rxjs';
import { RouterLink } from '@angular/router';
import {
  BlockedDate,
  BlockType,
} from '../../../shared/interfaces/restaurant.interface';

@Component({
  selector: 'app-dates-restaurant',
  imports: [
    MatIconModule,
    MatFormFieldModule,
    MatSelectModule,
    CalendarComponent,
    MatDividerModule,
    MatButtonModule,
    MatInputModule,
    MatRadioModule,
    FormsModule,
    RouterLink,
  ],
  templateUrl: './dates-restaurant.component.html',
  styleUrls: [
    './dates-restaurant.component.css',
    '../../../shared/styles/layout.css',
  ],
})
export class DatesRestaurantComponent implements OnInit {
  selected: string = 'select';
  hasSelectedDates: boolean = false;

  // Update eventos to include originalData
  eventos: Array<{
    date: string; // DD/MM/YYYY or raw selection format
    name: string; // For EVENT creation/edit (mapped to reason)
    blockType: BlockType | '';
    reason: string; // Backend reason
    originalData?: BlockedDate; // Original blocked date record when editing/deleting events
  }> = [];

  eventDays: BlockedDate[] = [];
  blockedDays: BlockedDate[] = [];
  dataLoaded: boolean = false;
  currentMonth: number = new Date().getMonth();
  currentYear: number = new Date().getFullYear();

  constructor(private restaurantService: RestaurantService) {}

  ngOnInit() {
    this.loadCalendarData();

    // Añadir un pequeño retraso para permitir que la UI se actualice
    setTimeout(() => {
      // Si el modo inicial es 'delete', cargar los eventos
      if (this.selected === 'delete') {
        this.updateEventsForCurrentMonth();
      }
    }, 300);
  }

  // Método para cargar datos del calendario
  loadCalendarData() {
    // Establecer estado de carga antes de hacer la llamada a la API
    this.dataLoaded = false;

    this.restaurantService.getBlockedDays().subscribe({
      next: (blocked: BlockedDate[]) => {
        this.blockedDays = blocked.filter(
          (day) =>
            day.block_type === 'HOLIDAY' || day.block_type === 'NO_SERVICE'
        );

        this.eventDays = blocked.filter((day) => day.block_type === 'EVENT');

        // console.log('Datos del calendario actualizados:', {
        //   blockedDays: this.blockedDays.length,
        //   eventDays: this.eventDays.length,
        // });

        // Asegurar que el calendario tiene tiempo para actualizarse
        setTimeout(() => {
          this.dataLoaded = true;
        }, 100);
      },
      error: (error) => {
        console.error('Error al obtener días bloqueados', error);
        this.dataLoaded = true;
      },
    });
  }

  // Método para el manejo de fechas seleccionadas
  onDatesSelected(selected: boolean) {
    this.hasSelectedDates = selected;

    if (this.selected === 'delete') {
      // Para el caso de eliminar, siempre mostrar todos los eventos del mes actual
      const currentMonth =
        this.calendarComponent?.month || new Date().getMonth();
      const currentYear =
        this.calendarComponent?.year || new Date().getFullYear();

      // Filtrar eventos para el mes actual
      const currentMonthEvents = this.eventDays.filter((eventDay) => {
        const eventDate = new Date(eventDay.date);
        return (
          eventDate.getMonth() === currentMonth &&
          eventDate.getFullYear() === currentYear
        );
      });

      // Si hay fechas seleccionadas, filtrar para mostrar solo eventos en esas fechas
      if (selected) {
        const selectedDates = this.calendarComponent?.getSelectedDates() || [];
        if (selectedDates.length > 0) {
          const filteredEvents: BlockedDate[] = [];

          for (const dateStr of selectedDates) {
            const dateObj = new Date(this.parseDate(dateStr));

            // Encontrar eventos coincidentes
            const matchingEvents = currentMonthEvents.filter(
              (eventDay: BlockedDate) => {
                const eventDate = new Date(eventDay.date);
                return (
                  eventDate.getDate() === dateObj.getDate() &&
                  eventDate.getMonth() === dateObj.getMonth() &&
                  eventDate.getFullYear() === dateObj.getFullYear()
                );
              }
            );

            filteredEvents.push(...matchingEvents);
          }

          if (filteredEvents.length > 0) {
            // Mapear al formato de eventos con originalData
            this.eventos = filteredEvents.map((event) => {
              return {
                date: this.formatDate(event.date),
                name: event.reason,
                blockType: 'EVENT',
                reason: event.reason,
                originalData: event,
              };
            });
          } else {
            // No se encontraron eventos para las fechas seleccionadas, seguir mostrando todos los eventos del mes
            this.eventos = currentMonthEvents.map((event) => {
              return {
                date: this.formatDate(event.date),
                name: event.reason,
                blockType: 'EVENT',
                reason: event.reason,
                originalData: event,
              };
            });
          }
        } else {
          // No hay fechas seleccionadas, mostrar todos los eventos del mes
          this.eventos = currentMonthEvents.map((event) => {
            return {
              date: this.formatDate(event.date),
              name: event.reason,
              blockType: 'EVENT',
              reason: event.reason,
              originalData: event,
            };
          });
        }
      } else {
        // Sin selección, mostrar todos los eventos del mes
        this.eventos = currentMonthEvents.map((event) => {
          return {
            date: this.formatDate(event.date),
            name: event.reason,
            blockType: 'EVENT',
            reason: event.reason,
            originalData: event,
          };
        });
      }

      // Siempre considerar que tener eventos es tener selección para el caso de eliminar
      this.hasSelectedDates = this.eventos.length > 0;
    } else if (selected) {
      // Mantener el código existente para otros casos (bloquear, añadir, etc.)
      const selectedDates = this.calendarComponent?.getSelectedDates() || [];
      // console.log('Fechas seleccionadas del calendario:', selectedDates);

      if (this.selected === 'block' || this.selected === 'add') {
        // Para casos de bloqueo y añadir, filtrar fechas ya bloqueadas/con eventos
        const availableDates = selectedDates.filter((dateStr) => {
          const dateObj = new Date(this.parseDate(dateStr));

          const isBlocked = this.blockedDays.some((blockedDay: BlockedDate) => {
            const blockedDate = new Date(blockedDay.date);
            return (
              blockedDate.getDate() === dateObj.getDate() &&
              blockedDate.getMonth() === dateObj.getMonth() &&
              blockedDate.getFullYear() === dateObj.getFullYear()
            );
          });

          const hasEvent = this.eventDays.some((eventDay: BlockedDate) => {
            const eventDate = new Date(eventDay.date);
            return (
              eventDate.getDate() === dateObj.getDate() &&
              eventDate.getMonth() === dateObj.getMonth() &&
              eventDate.getFullYear() === dateObj.getFullYear()
            );
          });

          // Para bloquear, no permitir días bloqueados o con eventos
          // Para añadir, no permitir días bloqueados o con eventos
          return !isBlocked && !hasEvent;
        });

        this.eventos = availableDates.map((date) => {
          return {
            date,
            name: '',
            blockType: this.selected === 'block' ? '' : 'EVENT', // Preestablecer blockType para el caso de añadir
            reason: '',
          };
        });

        this.hasSelectedDates = this.eventos.length > 0;
      } else {
        // Para otros casos, simplemente usar las fechas seleccionadas
        this.eventos = selectedDates.map((date) => {
          return {
            date,
            name: '',
            blockType: '',
            reason: '',
          };
        });
      }
    } else {
      // Para casos que no sean eliminar cuando no hay nada seleccionado
      this.eventos = [];
    }
  }

  // Método para escuchar cambios de mes
  onMonthChanged(event: { month: number; year: number }) {
    this.currentMonth = event.month;
    this.currentYear = event.year;

    // Si está en modo eliminar, actualizar los eventos mostrados para el nuevo mes
    if (this.selected === 'delete') {
      this.updateEventsForCurrentMonth();
    }
  }

  // Método para actualizar eventos del mes actual en modo de eliminación
  updateEventsForCurrentMonth() {
    // Filtrar eventos para el mes actual
    const currentMonthEvents = this.eventDays.filter(
      (eventDay: BlockedDate) => {
        const eventDate = new Date(eventDay.date);
        return (
          eventDate.getMonth() === this.currentMonth &&
          eventDate.getFullYear() === this.currentYear
        );
      }
    );

    // Convertir al formato de eventos con datos originales
    this.eventos = currentMonthEvents.map((event) => {
      return {
        date: this.formatDate(event.date),
        name: event.reason,
        blockType: 'EVENT',
        reason: event.reason,
        originalData: event,
      };
    });

    this.hasSelectedDates = this.eventos.length > 0;
  }

  // Método auxiliar para analizar cadenas de fecha de manera consistente
  parseDate(dateString: string): Date {
    // Analizar fechas en formato DD/MM/AAAA
    if (dateString.includes('/')) {
      const parts = dateString.split('/');
      if (parts.length === 3) {
        const day = parseInt(parts[0], 10);
        const month = parseInt(parts[1], 10) - 1; // Meses basados en 0
        const year = parseInt(parts[2], 10);
        return new Date(year, month, day);
      }
    }
    return new Date(dateString);
  }

  // Método para validar si un evento es válido
  isEventoValid(evento: {
    date: string;
    name: string;
    blockType: BlockType | '';
    reason: string;
  }): boolean {
    return (
      evento.date !== '' && evento.blockType !== '' && evento.reason !== ''
    );
  }

  // Método para validar si todos los eventos son válidos
  areAllEventosValid(): boolean {
    if (this.eventos.length === 0) return false;
    return this.eventos.every((evento) => this.isEventoValid(evento));
  }

  // Método de validación para nombres de eventos
  areAllEventNamesValid(): boolean {
    if (this.eventos.length === 0) return false;
    return this.eventos.every(
      (evento) => evento.name && evento.name.trim() !== ''
    );
  }

  @ViewChild(CalendarComponent) calendarComponent!: CalendarComponent;
  resetCalendarSelection() {
    if (this.calendarComponent) {
      this.calendarComponent.clearSelectedDays();
    }
  }
  readonly dialog = inject(MatDialog);

  // Método para bloquear fechas
  block() {
    if (!this.hasSelectedDates || this.eventos.length === 0) {
      console.warn('No hay fechas seleccionadas para bloquear.');
      return;
    }

    if (!this.areAllEventosValid()) {
      console.warn(
        'Todas las fechas deben tener tipo de bloqueo y razón especificados.'
      );
      return;
    }

    // Crear una función manejadora que será llamada cuando el usuario confirme
    const blockHandler = () => {
      return new Observable((observer) => {
        const blockPromises = this.eventos.map((evento) => {
          const formattedDate = this.formatDateForAPI(evento.date);

          if (!formattedDate) {
            console.error(`Formato de fecha inválido: ${evento.date}`);
            return Promise.reject(`Formato de fecha inválido: ${evento.date}`);
          }

          // console.log('Fecha original:', evento.date);
          // console.log('Fecha formateada para API:', formattedDate);

          const payload = {
            date: formattedDate,
            block_type: evento.blockType as BlockType,
            reason: evento.reason,
          };

          return this.restaurantService.postBlockedDate(payload).toPromise();
        });

        Promise.all(blockPromises)
          .then(() => {
            // console.log('Todas las fechas bloqueadas exitosamente');
            // Limpiar cualquier fecha seleccionada
            this.resetCalendarSelection();
            // Reiniciar estado del formulario
            this.hasSelectedDates = false;
            this.eventos = [];
            // Reiniciar el desplegable de selección
            this.selected = 'select';

            // Recargar datos del calendario para mostrar las fechas recién bloqueadas
            this.dataLoaded = false; // Mostrar indicador de carga
            this.loadCalendarData();

            // Notificar al observador que la operación tuvo éxito
            observer.next(true);
            observer.complete();
          })
          .catch((error) => {
            console.error('Error al bloquear fechas:', error);
            observer.error(error);
          });
      });
    };

    // Abrir el diálogo con el manejador
    const dialogRef = this.dialog.open(EditDataDialogComponent, {
      width: '550px',
      data: {
        title: '¿Seguro que quiere bloquear estas fechas?',
        confirmText: 'Bloquear',
        cancelText: 'Cancelar',
        selectedDates: this.eventos.map((e) => this.formatDate(e.date)),
        handler: blockHandler,
        successMessage: 'Fechas bloqueadas correctamente',
      },
    });
  }

  // Método para desbloquear fechas
  unblock() {
    const selectedDates = this.calendarComponent?.getSelectedDates() || [];

    if (selectedDates.length === 0) {
      console.warn('No hay fechas seleccionadas para desbloquear.');
      return;
    }

    // Crear una función manejadora que será llamada cuando el usuario confirme
    const unblockHandler = () => {
      return new Observable((observer) => {
        // Crear un array de promesas para cada fecha a desbloquear
        const unblockPromises = selectedDates.map((dateStr) => {
          // Formatear fecha para API (AAAA-MM-DD) y como parámetro ID
          const formattedDate = this.formatDateForAPI(dateStr);

          if (!formattedDate) {
            console.error(`Formato de fecha inválido: ${dateStr}`);
            return Promise.reject(`Formato de fecha inválido: ${dateStr}`);
          }

          // Crear payload para desbloqueo
          const payload = {
            is_deleted: true,
            deleted_at: new Date().toISOString(), // Marca de tiempo actual en formato ISO
          };

          // console.log(`Desbloqueando fecha: ${formattedDate}`);

          // Llamar al método unblockDate con la fecha formateada como ID
          return this.restaurantService
            .unblockDate(payload, formattedDate)
            .toPromise();
        });

        // Ejecutar todas las promesas
        Promise.all(unblockPromises)
          .then(() => {
            // console.log('Todas las fechas desbloqueadas exitosamente');
            // Reiniciar selección y recargar calendario
            this.resetCalendarSelection();
            this.hasSelectedDates = false;
            this.eventos = [];
            this.selected = 'select';

            // Recargar datos del calendario para mostrar los cambios
            this.dataLoaded = false;
            this.loadCalendarData();

            // Notificar al observador
            observer.next(true);
            observer.complete();
          })
          .catch((error) => {
            console.error('Error al desbloquear fechas:', error);
            observer.error(error);
          });
      });
    };

    // Abrir diálogo de confirmación
    const dialogRef = this.dialog.open(EditDataDialogComponent, {
      width: '550px',
      data: {
        title: '¿Seguro que quiere desbloquear estas fechas?',
        confirmText: 'Desbloquear',
        cancelText: 'Cancelar',
        selectedDates: selectedDates.map((dateStr) => this.formatDate(dateStr)),
        handler: unblockHandler,
        successMessage: 'Fechas desbloqueadas correctamente',
      },
    });
  }

  // Método utilitario para formatear fechas para la API
  formatDateForAPI(dateString: string): string {
    try {
      // Verificar el formato de la cadena de fecha
      if (dateString.includes('/')) {
        // Formato: DD/MM/AAAA
        const parts = dateString.split('/');
        if (parts.length === 3) {
          const day = parts[0].padStart(2, '0');
          const month = parts[1].padStart(2, '0');
          const year = parts[2];
          return `${year}-${month}-${day}`;
        }
      }

      // Intentar análisis estándar de fecha como fallback
      const date = new Date(dateString);
      if (!isNaN(date.getTime())) {
        // Objeto de fecha válido
        return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(
          2,
          '0'
        )}-${String(date.getDate()).padStart(2, '0')}`;
      }

      // Si todo falla, registrar error y devolver cadena vacía
      console.error(`No se puede analizar la fecha: ${dateString}`);
      return '';
    } catch (error) {
      console.error(`Error al formatear la fecha: ${dateString}`, error);
      return '';
    }
  }

  // Formatear fecha para visualización
  formatDate(dateString: string): string {
    try {
      const date = new Date(dateString);
      if (!isNaN(date.getTime())) {
        const day = date.getDate().toString().padStart(2, '0');
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const year = date.getFullYear();
        return `${day}/${month}/${year}`;
      }
      return dateString; // Devolver tal cual si el análisis falla
    } catch (error) {
      console.error(
        `Error al formatear fecha para visualización: ${dateString}`,
        error
      );
      return dateString;
    }
  }

  // Método para añadir eventos
  addEvent() {
    if (!this.hasSelectedDates || this.eventos.length === 0) {
      console.warn('No hay fechas seleccionadas para añadir eventos.');
      return;
    }

    if (!this.areAllEventNamesValid()) {
      console.warn('Todos los eventos deben tener un nombre asignado.');
      return;
    }

    // Crear una función manejadora para el diálogo
    const addEventHandler = () => {
      return new Observable((observer) => {
        const eventPromises = this.eventos.map((evento) => {
          const formattedDate = this.formatDateForAPI(evento.date);

          if (!formattedDate) {
            console.error(`Formato de fecha inválido: ${evento.date}`);
            return Promise.reject(`Formato de fecha inválido: ${evento.date}`);
          }

          // Crear payload para evento - usando EVENT como block_type y name como reason
          const payload = {
            date: formattedDate,
            block_type: 'EVENT' as BlockType,
            reason: evento.name,
          };

          // console.log('Añadiendo evento:', payload);

          // Usar el mismo método que para bloquear fechas pero con tipo EVENT
          return this.restaurantService.postBlockedDate(payload).toPromise();
        });

        Promise.all(eventPromises)
          .then(() => {
            // console.log('Todos los eventos añadidos exitosamente');
            // Reiniciar selección y recargar calendario
            this.resetCalendarSelection();
            this.hasSelectedDates = false;
            this.eventos = [];
            this.selected = 'select';

            // Recargar datos del calendario para mostrar los nuevos eventos
            this.dataLoaded = false;
            this.loadCalendarData();

            // Notificar al observador
            observer.next(true);
            observer.complete();
          })
          .catch((error) => {
            console.error('Error al añadir eventos:', error);
            observer.error(error);
          });
      });
    };

    // Abrir diálogo de confirmación
    const dialogRef = this.dialog.open(EditDataDialogComponent, {
      width: '550px',
      data: {
        title: '¿Seguro que quiere añadir estos eventos?',
        confirmText: 'Añadir',
        cancelText: 'Cancelar',
        selectedDates: this.eventos.map((e) => `${this.formatDate(e.date)}`),
        handler: addEventHandler,
        successMessage: 'Eventos añadidos correctamente',
      },
    });
  }

  // Método para eliminar un evento individual
  deleteEvent(evento: any) {
    if (!evento || !evento.date) {
      console.warn('No hay evento para eliminar.');
      return;
    }

    // Crear un diálogo de confirmación
    const dialogRef = this.dialog.open(EditDataDialogComponent, {
      width: '550px',
      data: {
        title: '¿Seguro que quiere eliminar este evento?',
        message: `Se eliminará el evento "${evento.name}" del día ${evento.date}`,
        confirmText: 'Eliminar',
        cancelText: 'Cancelar',
        handler: () => {
          return new Observable((observer) => {
            const formattedDate = this.formatDateForAPI(evento.date);

            if (!formattedDate) {
              console.error(`Formato de fecha inválido: ${evento.date}`);
              observer.error(`Formato de fecha inválido: ${evento.date}`);
              return;
            }

            // Crear payload para eliminación
            const payload = {
              is_deleted: true,
              deleted_at: new Date().toISOString(),
            };

            // console.log(
            //   `Eliminando evento: ${evento.name} en ${formattedDate}`
            // );

            // Usar putBlockedDate como se solicitó
            this.restaurantService
              .putBlockedDate(payload, formattedDate)
              .toPromise()
              .then(() => {
                // console.log('Evento eliminado exitosamente');

                // Reiniciar selección del calendario
                this.resetCalendarSelection();

                // Reiniciar estado del formulario
                this.hasSelectedDates = false;
                this.eventos = [];

                // Cambiar el modo a 'select' para reiniciar completamente
                this.selected = 'select';

                // Recargar datos del calendario
                this.dataLoaded = false;
                this.loadCalendarData();

                observer.next(true);
                observer.complete();
              })
              .catch((error) => {
                console.error('Error al eliminar evento:', error);
                observer.error(error);
              });
          });
        },
        successMessage: 'Evento eliminado correctamente',
      },
    });
  }

  // Método para guardar cambios en todos los eventos
  saveEventChanges() {
    if (this.eventos.length === 0) {
      console.warn('No hay eventos para guardar.');
      return;
    }

    // Verificar que todos los eventos tengan nombres
    if (!this.areAllEventNamesValid()) {
      console.warn('Todos los eventos deben tener un nombre asignado.');
      return;
    }

    // Encontrar eventos que han sido cambiados comparando con originalData
    const changedEvents = this.eventos.filter((evento) => {
      // Verificar si originalData existe y si la razón es diferente del nombre actual
      return evento.originalData && evento.originalData.reason !== evento.name;
    });

    if (changedEvents.length === 0) {
      // console.log('No hay cambios para guardar.');
      // Mostrar un mensaje al usuario de que no hay cambios
      const dialogRef = this.dialog.open(EditDataDialogComponent, {
        width: '550px',
        data: {
          title: 'Sin cambios',
          message: 'No se han detectado cambios en los eventos.',
          confirmText: 'Aceptar',
          hideCancel: true,
        },
      });
      return;
    }

    // Crear una función manejadora para el diálogo
    const updateEventsHandler = () => {
      return new Observable((observer) => {
        const updatePromises = changedEvents.map((evento) => {
          const formattedDate = this.formatDateForAPI(evento.date);

          if (!formattedDate) {
            console.error(`Formato de fecha inválido: ${evento.date}`);
            return Promise.reject(`Formato de fecha inválido: ${evento.date}`);
          }

          // Crear payload para actualización - solo necesitamos actualizar la razón (nombre)
          const payload = {
            reason: evento.name,
          };

          // console.log('Actualizando evento:', formattedDate, payload);

          // Usar putBlockedDate para actualizar
          return this.restaurantService
            .putBlockedDate(payload, formattedDate)
            .toPromise();
        });

        Promise.all(updatePromises)
          .then(() => {
            // console.log('Todos los eventos actualizados exitosamente');

            // Reiniciar selección del calendario
            this.resetCalendarSelection();

            // Reiniciar estado del formulario
            this.hasSelectedDates = false;
            this.eventos = [];

            // Cambiar el modo a 'select' para reiniciar completamente
            this.selected = 'select';

            // Recargar datos del calendario
            this.dataLoaded = false;
            this.loadCalendarData();

            // Notificar al observador
            observer.next(true);
            observer.complete();
          })
          .catch((error) => {
            console.error('Error al actualizar eventos:', error);
            observer.error(error);
          });
      });
    };

    // Abrir diálogo de confirmación
    const dialogRef = this.dialog.open(EditDataDialogComponent, {
      width: '550px',
      data: {
        title: '¿Guardar cambios en los eventos?',
        message: `Se actualizarán ${changedEvents.length} evento(s)`,
        confirmText: 'Guardar',
        cancelText: 'Cancelar',
        handler: updateEventsHandler,
        successMessage: 'Eventos actualizados correctamente',
      },
    });
  }

  // Añadir este método para manejar los cambios en el selector de modo
  onModeChange(mode: string) {
    // Guardar el modo anterior para posible comparación
    const prevMode = this.selected;

    // Actualizar el modo actual
    this.selected = mode;

    // Si se cambió al modo 'delete', inicializar la vista de eventos
    if (mode === 'delete') {
      // Asegurar que los datos están disponibles
      if (this.dataLoaded) {
        // Actualizar inmediatamente los eventos para el mes actual
        this.updateEventsForCurrentMonth();
      } else {
        // Si los datos no están cargados, esperar a que se carguen
        const subscription = this.restaurantService.getBlockedDays().subscribe({
          next: (blocked) => {
            this.blockedDays = blocked.filter(
              (day) =>
                day.block_type === 'HOLIDAY' || day.block_type === 'NO_SERVICE'
            );
            this.eventDays = blocked.filter(
              (day) => day.block_type === 'EVENT'
            );

            // Ahora que tenemos los datos, actualizar los eventos
            this.updateEventsForCurrentMonth();
            this.dataLoaded = true;
            subscription.unsubscribe();
          },
          error: (error) => {
            console.error(
              'Error al obtener días bloqueados para inicializar modo delete',
              error
            );
            this.dataLoaded = true;
            subscription.unsubscribe();
          },
        });
      }
    } else {
      // Si cambiamos desde 'delete' a otro modo, limpiar eventos
      if (prevMode === 'delete') {
        this.eventos = [];
        this.hasSelectedDates = false;
      }
    }
  }
}
